import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, Heart, Flame, ChevronRight, ArrowRight } from 'lucide-react';
import SectionTitle from '../components/ui/SectionTitle';
import { foodTruckInfo, badges, schedule, featuredDishes, reviews, images } from '../data/mock';

const HomePage = () => {
  const homeReviews = reviews.slice(0, 6);
  const openDays = schedule.filter(day => day.open);

  const BadgeIcon = ({ icon }) => {
    const icons = { Star, Heart, Flame, MapPin };
    const IconComponent = icons[icon];
    return IconComponent ? <IconComponent size={18} /> : null;
  };

  return (
    <main className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Street food"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E0E0E] via-[#0E0E0E]/90 to-[#0E0E0E]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7C948]/10 border border-[#F7C948]/30 text-[#F7C948] text-sm font-medium mb-6">
                <Flame size={16} />
                Street-Food Gourmande
              </div>

              {/* Title */}
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-4 leading-tight">
                Just'Une{' '}
                <span className="text-gradient-yellow">Dalle</span>
              </h1>
              <p className="text-xl text-gray-400 mb-2">Food Truck</p>
              <p className="text-2xl md:text-3xl text-gray-200 font-light mb-8 leading-relaxed">
                {foodTruckInfo.slogan}
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4 mb-10">
                <a
                  href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
                  className="btn-primary flex items-center gap-2 text-lg"
                >
                  <Phone size={20} />
                  Appeler maintenant
                </a>
                <Link
                  to="/menu"
                  className="btn-secondary flex items-center gap-2 text-lg"
                >
                  Voir le menu
                  <ArrowRight size={20} />
                </Link>
              </div>

              {/* Badges */}
              <div className="grid grid-cols-2 gap-3">
                {badges.map((badge, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                      badge.color === 'yellow'
                        ? 'bg-[#F7C948]/10 border border-[#F7C948]/20'
                        : badge.color === 'red'
                        ? 'bg-[#D94848]/10 border border-[#D94848]/20'
                        : 'bg-white/5 border border-white/10'
                    }`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <span className={`${
                      badge.color === 'yellow' ? 'text-[#F7C948]' :
                      badge.color === 'red' ? 'text-[#D94848]' : 'text-white'
                    }`}>
                      <BadgeIcon icon={badge.icon} />
                    </span>
                    <span className="text-sm font-medium text-gray-200">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="hidden lg:block animate-fade-in delay-300">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#F7C948]/20 rounded-3xl blur-3xl" />
                <img
                  src={images.burger1}
                  alt="Burger signature"
                  className="relative w-full h-[500px] object-cover rounded-3xl shadow-2xl"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-6 -left-6 bg-[#0E0E0E] border border-[#F7C948]/30 rounded-2xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className="text-[#F7C948] fill-[#F7C948]" />
                      ))}
                    </div>
                    <span className="text-white font-semibold">5/5</span>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">Clients satisfaits</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#F7C948] rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Featured Dishes */}
      <section className="section-padding bg-[#0E0E0E] relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F7C948]/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <SectionTitle
            badge="Les favoris"
            title="Nos Incontournables"
            subtitle="Découvrez nos plats signature préparés avec passion et des ingrédients frais"
          />

          <div className="grid md:grid-cols-3 gap-8">
            {featuredDishes.map((dish, index) => (
              <div
                key={dish.id}
                className="group bg-[#1A1A1A] rounded-2xl overflow-hidden border border-white/5 hover:border-[#F7C948]/30 transition-all duration-500 hover-lift"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={dish.image}
                    alt={dish.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent opacity-60" />
                  <div className="absolute top-4 right-4 bg-[#F7C948] text-[#0E0E0E] px-3 py-1 rounded-lg font-bold text-sm">
                    {dish.price}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold text-white mb-2 group-hover:text-[#F7C948] transition-colors duration-300">
                    {dish.name}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {dish.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 text-[#F7C948] font-semibold hover:gap-4 transition-all duration-300"
            >
              Voir tout le menu
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Schedule & Location */}
      <section className="section-padding bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Schedule */}
            <div>
              <SectionTitle
                title="Horaires & Emplacement"
                subtitle="Retrouvez-nous à Malafretaz"
                centered={false}
              />

              <div className="bg-[#0E0E0E] rounded-2xl p-6 border border-white/10 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#F7C948]/10 rounded-xl flex items-center justify-center">
                    <Clock size={24} className="text-[#F7C948]" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">Nos horaires</h3>
                    <p className="text-sm text-gray-400">Service le soir</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {schedule.map((item) => (
                    <div
                      key={item.day}
                      className={`flex justify-between items-center py-2 border-b border-white/5 last:border-0 ${
                        item.open ? '' : 'opacity-50'
                      }`}
                    >
                      <span className="text-gray-300">{item.day}</span>
                      <span className={`font-medium ${item.open ? 'text-[#F7C948]' : 'text-gray-500'}`}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0E0E0E] rounded-2xl p-6 border border-white/10">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-[#F7C948]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-[#F7C948]" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white mb-1">Notre adresse</h3>
                    <p className="text-gray-400 mb-4">{foodTruckInfo.address}</p>
                    <a
                      href="https://maps.google.com/?q=429+Route+de+Bourg+01340+Malafretaz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#F7C948] font-medium hover:gap-3 transition-all duration-300"
                    >
                      Voir l'itinéraire
                      <ChevronRight size={18} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Location CTA */}
            <div className="relative">
              <div className="bg-[#0E0E0E] rounded-2xl p-8 border border-[#F7C948]/20 h-full flex flex-col justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#F7C948]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MapPin size={40} className="text-[#F7C948]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white mb-2">
                    Venez nous voir !
                  </h3>
                  <p className="text-gray-400 mb-6">
                    {foodTruckInfo.address}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="https://maps.google.com/?q=429+Route+de+Bourg+01340+Malafretaz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-[#F7C948] text-[#0E0E0E] px-6 py-3 rounded-xl font-semibold hover:bg-[#FFE17D] transition-all duration-300"
                    >
                      <MapPin size={18} />
                      Voir l'itinéraire
                    </a>
                    <a
                      href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300"
                    >
                      <Phone size={18} />
                      Appeler
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section-padding bg-[#0E0E0E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle
            badge="Témoignages"
            title="Avis de nos clients"
            subtitle="Ce que nos clients disent de nous"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeReviews.map((review, index) => (
              <div
                key={review.id}
                className="bg-[#1A1A1A] rounded-2xl p-6 border border-white/5 hover:border-[#F7C948]/20 transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < review.rating ? 'text-[#F7C948] fill-[#F7C948]' : 'text-gray-600'}
                    />
                  ))}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#F7C948]/20 rounded-full flex items-center justify-center">
                      <span className="text-[#F7C948] font-semibold text-sm">
                        {review.author.charAt(0)}
                      </span>
                    </div>
                    <span className="text-white font-medium">{review.author}</span>
                  </div>
                  <span className="text-gray-500 text-xs">{review.date}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/avis"
              className="inline-flex items-center gap-2 text-[#F7C948] font-semibold hover:gap-4 transition-all duration-300"
            >
              Voir tous les avis
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1A1A1A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#F7C948]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D94848]/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
            Une envie de <span className="text-gradient-yellow">street-food</span> ?
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            Appelez-nous ou passez nous voir à Malafretaz !
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
              className="btn-primary flex items-center gap-2 text-lg"
            >
              <Phone size={20} />
              {foodTruckInfo.phone}
            </a>
            <Link
              to="/emplacements"
              className="btn-secondary flex items-center gap-2 text-lg"
            >
              <MapPin size={20} />
              Nous trouver
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
